// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — render mode (docs/AYNI_XR_STUDIO_TECH_DESIGN.md §4, plan J6).
 *
 * `?render=1` turns the app into a frame-by-frame renderer for a GPU worker:
 * no interface, one shot, and a stepping protocol modelled on the hero
 * renderer (`scripts/hero/hero.html`):
 *
 *   window.aynRender.ready        → { renderer, frames, width, height }
 *   window.aynRender.renderFrame(i) → data URL of frame i, composited
 *
 * Each frame: camera from the shot's keyframes with `jumpTo` (no animation),
 * the data date → the dataset video's time, then the frame is waited for
 * until it is really on the globe (`seeked`, then the earth layer reports
 * the uploaded frame at that time, the map has every tile of the new view,
 * then a fresh render), and only then read from the map's canvas. Without
 * the tiles a still, or a video's first frames, came out with part of the
 * basemap missing. The interface is never in the picture: the
 * canvas pixels are read directly and the credit, legend, branding and
 * captions are drawn by the compositor.
 *
 * A worker must never deliver a software-rendered clip, so `ready` rejects
 * when WebGL runs on SwiftShader (proven flags: ANGLE on Vulkan).
 *
 * A transparent shot (ProRes 4444 or PNG, plan J7) hides the stars and the
 * atmosphere, so everything but the globe and the overlays has alpha 0.
 *
 * The app's pieces come in through {@link RenderHost}, so this module is
 * tested without a browser.
 *
 * DSA 1.8 vector layers (quakes entering and fading) run on the render's own
 * clock (notes §6): after a moment is on the globe, `vectorStep` advances
 * their animations by the shot time since the last moment (1 / outputFps,
 * or less between motion-blur samples), never by wall time, so a render
 * looks like live playback at the same rate however long a frame takes.
 * Their data time is the moment's own date, continuous, not the video's:
 * the video is seeked to the frame that holds the date, so within one
 * frame its time stands still while the quakes of that day go on entering.
 *
 * A version 2 shot (stops, `film.ts`) also changes stop: when a frame's data
 * belongs to another stop, `applyStop` puts that stop's dataset, layers and
 * colours on the globe first, and data moves (crossfade, wipe, through the
 * basemap) go to `setDataMix`. With motion blur a frame is the average of
 * several moments around it, skipped where nothing moves.
 */

import { frameCount, frameTime, MOTION_BLUR_SAMPLES, outputSize, renderFps, sampleShot, type Shot, type ShotSample, type ShotView } from './shot'
import type { FilmData, FilmStop } from './film'
import { compareSplit } from './effects'

export interface RenderHost {
  /** Move the camera at once (MapLibre `jumpTo`). */
  jumpTo(view: ShotView): void
  /** The primary dataset's video, or null for a still dataset. */
  video(): HTMLVideoElement | null
  /** The video time showing the data at `ms`, or null when the stream has no axis. */
  videoTimeForDateMs(ms: number): number | null
  /** The frame time the earth layer last uploaded (`mapRenderer.getUploadedFrameTime`). */
  uploadedFrameTime(): number | null
  /** Repaint and wait for the render (`mapRenderer.triggerFreshRender`). */
  freshRender(): Promise<void>
  /** Whether the map has every tile it needs for the current view (MapLibre `areTilesLoaded`). */
  tilesLoaded(): boolean
  /** The map's WebGL canvas. */
  canvas(): HTMLCanvasElement | null
  /** The WebGL renderer string, e.g. "ANGLE (NVIDIA, Vulkan …)". */
  webglRenderer(): string
  /**
   * Before the first frame: raster tiles drawn at once (no fade-in, or a tile
   * just loaded is still invisible), the earth layer's textures (clouds,
   * night lights) loaded, and the stars loaded, or, for a transparent shot,
   * space left transparent: no stars, no atmosphere (a ProRes 4444 or PNG with
   * alpha). Without it a video's first frames lacked tiles, clouds and stars.
   */
  prepare(options: { transparent: boolean }): Promise<void>
  /**
   * Upload the dataset video's current frame to the globe again, whatever
   * time the last upload had (`requestVideoUpdate`). An upload made before
   * the decoder had a frame for that time is empty, and with the date held
   * nothing would replace it: every frame of the video lost its data.
   */
  reuploadFrame?(): void
  /**
   * AYNI (multi-globe plan, a split render): whether every other globe shows
   * the frame of its own date (they follow the main globe's seek, each by its
   * time rule); asks a stalled one to upload again. Absent: one globe.
   */
  globesReady?(): boolean
  /** Version 2: put a stop's dataset, layers and colours on the globe (not its camera or date). */
  applyStop?(stop: FilmStop): Promise<void>
  /** Version 2: a data move under way (an incoming stop drawn over, or the dataset faded); the plain state when none. */
  setDataMix?(data: FilmData): Promise<void>
  /** Version 2: a split compare in a stop's hold — the same dataset at `atMs` right of `split` (0–1 across); null: none. */
  setCompare?(compare: { atMs: number; split: number } | null): Promise<void>
  /**
   * DSA 1.8: advance every globe's vector layers by `seconds` of shot time
   * (0 on the first moment), at the moment's date `atMs` plus `playSeconds`
   * of video played through a stop, and wait until they are drawn. Absent: none.
   */
  vectorStep?(seconds: number, at: { atMs: number | null; playSeconds: number }): Promise<void>
}

export interface FrameCompositor {
  /**
   * Draw frame `i`'s overlays (credit, legend, branding, captions, a stop's
   * effects) onto `ctx`, sized to the output; `clean` is the frame before
   * any overlay (what blur and grey are made from).
   */
  draw(ctx: CanvasRenderingContext2D, i: number, clean?: HTMLCanvasElement): void
}

export interface RenderController {
  ready: Promise<{ renderer: string; frames: number; width: number; height: number }>
  renderFrame(i: number): Promise<string>
}

/** How close the uploaded frame must be to the target, in video seconds (well under one frame). */
const FRAME_EPSILON_S = 0.002
/** How long a seek waits for its frame to be presented before uploading anyway. */
const PRESENT_TIMEOUT_MS = 2000
/** Give up on a frame that never paints, rather than hang the worker. */
const PAINT_TIMEOUT_MS = 15_000
const POLL_MS = 16

export class SoftwareRendererError extends Error {
  constructor(renderer: string) {
    super(`refusing to render on software WebGL: ${renderer}`)
    this.name = 'SoftwareRendererError'
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * Seek the dataset video to `target` and wait until that frame is on the
 * globe: the video has seeked, the earth layer has uploaded a frame at that
 * time, and a fresh render has run. A still dataset (no video) only renders.
 */
export async function waitForPaintedFrame(host: RenderHost, target: number | null, timeoutMs = PAINT_TIMEOUT_MS): Promise<void> {
  const video = host.video()
  if (video && target !== null && Number.isFinite(target)) {
    const clamped = Math.max(0, Math.min(target, Number.isFinite(video.duration) && video.duration > 0 ? video.duration : target))
    const onGlobe = () => {
      const uploaded = host.uploadedFrameTime()
      return uploaded !== null && Math.abs(uploaded - video.currentTime) <= FRAME_EPSILON_S
    }
    // The earth layer uploads a frame after a `seeked`, so seek whenever the globe does not
    // show the target yet — even when the video's clock already reads it (a seek to the same
    // time still fires `seeked`): an earlier seek may have ended before the layer was ready.
    // `seeked` can come before the frame is there to upload (first found on the
    // render GPU, 10 October 2026: a video held on one date showed no data at
    // all): a seek also waits for the frame to be presented, then uploads it.
    const seek = async () => {
      const seeked = new Promise<void>((resolve) => video.addEventListener('seeked', () => resolve(), { once: true }))
      const presented = new Promise<void>((resolve) => {
        const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }
        if (typeof v.requestVideoFrameCallback === 'function') v.requestVideoFrameCallback(() => resolve())
        else resolve()
      })
      video.currentTime = clamped
      await Promise.race([seeked, sleep(timeoutMs)])
      await Promise.race([presented, sleep(PRESENT_TIMEOUT_MS)])
      host.reuploadFrame?.()
    }
    if (Math.abs(video.currentTime - clamped) > FRAME_EPSILON_S || video.seeking || !onGlobe()) await seek()
    const deadline = Date.now() + timeoutMs
    let reseekAt = Date.now() + Math.min(2000, timeoutMs / 2)
    for (;;) {
      await host.freshRender()
      if (onGlobe() && host.tilesLoaded() && (host.globesReady?.() ?? true)) break
      if (Date.now() > deadline) throw new Error(`frame at ${clamped.toFixed(3)} s never reached the globe`)
      // A data frame that stalls gets one more seek (not missing tiles: they only need time).
      if (!onGlobe() && Date.now() > reseekAt) { reseekAt = Infinity; await seek() }
      await sleep(POLL_MS)
    }
  } else {
    const deadline = Date.now() + timeoutMs
    while (!host.tilesLoaded() || !(host.globesReady?.() ?? true)) {
      if (Date.now() > deadline) throw new Error('the map tiles never finished loading')
      await host.freshRender()
      await sleep(POLL_MS)
    }
  }
  // The frame on screen uploaded once more before the picture is taken: a
  // video held on one date otherwise keeps whatever its first upload got.
  if (video) host.reuploadFrame?.()
  await host.freshRender()
}

/** Prepare a shot for rendering and return the stepping protocol. */
export function createRenderController(
  host: RenderHost,
  shot: Shot,
  compositor: FrameCompositor | null,
  makeCanvas: (w: number, h: number) => HTMLCanvasElement = (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h }),
): RenderController {
  const { width, height } = outputSize(shot.output.aspect, shot.output.resolution)
  const frames = frameCount(shot.output)
  const ready = (async () => {
    const renderer = host.webglRenderer()
    if (/swiftshader|llvmpipe|software/i.test(renderer)) throw new SoftwareRendererError(renderer)
    await host.prepare({ transparent: shot.output.transparent })
    return { renderer, frames, width, height }
  })()

  let out: HTMLCanvasElement | null = null
  let clean: HTMLCanvasElement | null = null
  let appliedStop = -1
  let mixed = false
  let compared = false
  /** Shot time of the last moment shown, for the vector clock. */
  let lastMoment: number | null = null

  /** Put the globe in the state of one moment (shot time `at`) and wait until it is painted. */
  async function showMoment(sample: ShotSample, at: number): Promise<void> {
    const film = sample.film
    if (film && shot.film) {
      if (film.data.stop !== appliedStop) {
        await host.applyStop?.(shot.film.stops[film.data.stop])
        appliedStop = film.data.stop
      }
      // A data move under way, or back to the plain state once one has run.
      const moving = film.data.incoming !== null || film.data.opacity < 1
      if (host.setDataMix && (moving || mixed)) { await host.setDataMix(film.data); mixed = moving }
      // A split compare while a stop holds.
      const stop = shot.film.stops[film.stop]
      const otherMs = film.phase === 'hold' && stop.effects.compare?.at ? Date.parse(stop.effects.compare.at) : NaN
      if (host.setCompare && (Number.isFinite(otherMs) || compared)) {
        compared = Number.isFinite(otherMs)
        await host.setCompare(compared ? { atMs: otherMs, split: compareSplit(stop.effects.compare!, { local: film.local, hold: stop.hold }) } : null)
      }
    }
    host.jumpTo(sample.view)
    const base = sample.atMs === null ? null : host.videoTimeForDateMs(sample.atMs)
    const target = base === null ? null : base + (sample.film?.data.playSeconds ?? 0)
    await waitForPaintedFrame(host, target)
    if (host.vectorStep) {
      // Frames are rendered in order; a step back (a retried frame) restarts the clock as a seek would.
      const dt = lastMoment === null || at < lastMoment ? 0 : at - lastMoment
      lastMoment = at
      await host.vectorStep(dt, { atMs: sample.atMs, playSeconds: sample.film?.data.playSeconds ?? 0 })
    }
  }

  /** The moments one frame is made of: one, or several around it with motion blur where the picture moves. */
  function momentsOf(i: number): number[] {
    const t = frameTime(shot.output, i)
    if (!shot.finish.motionBlur || shot.output.format === 'png') return [t]
    const span = 1 / renderFps(shot.output)
    const a = sampleShot(shot, t), b = sampleShot(shot, t + span)
    const still = Math.abs(a.view.lat - b.view.lat) < 1e-6 && Math.abs(a.view.lon - b.view.lon) < 1e-6
      && Math.abs(a.view.zoom - b.view.zoom) < 1e-6 && Math.abs(a.view.bearing - b.view.bearing) < 1e-6
    if (still) return [t]
    // Spread over the shutter (half the frame), centred on the frame's own moment.
    return Array.from({ length: MOTION_BLUR_SAMPLES }, (_, k) => t + span * 0.5 * (k / (MOTION_BLUR_SAMPLES - 1) - 0.5))
  }

  return {
    ready,
    async renderFrame(i: number): Promise<string> {
      await ready
      if (!Number.isInteger(i) || i < 0 || i >= frames) throw new RangeError(`frame ${i} is outside 0–${frames - 1}`)
      out ??= makeCanvas(width, height)
      const ctx = out.getContext('2d')
      if (!ctx) throw new Error('no 2D context for compositing')
      // Each frame starts afresh: transparent where the shot keeps space transparent, else black
      // (the canvas leaves empty space transparent, which a PNG still would keep).
      if (shot.output.transparent) ctx.clearRect(0, 0, width, height)
      else { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, width, height) }
      const moments = momentsOf(i)
      for (let k = 0; k < moments.length; k++) {
        await showMoment(sampleShot(shot, moments[k]), moments[k])
        const source = host.canvas()
        if (!source) throw new Error('the map has no canvas')
        // A running average: the k-th moment weighs 1/(k+1) over the ones before.
        ctx.save()
        ctx.globalAlpha = 1 / (k + 1)
        ctx.drawImage(source, 0, 0, width, height)
        ctx.restore()
      }
      if (compositor) {
        // The clean frame, before any overlay: what a stop's blur and grey are made from.
        clean ??= makeCanvas(width, height)
        const c = clean.getContext('2d')
        if (c) { c.clearRect(0, 0, width, height); c.drawImage(out, 0, 0) }
        compositor.draw(ctx, i, clean)
      }
      return out.toDataURL('image/png')
    },
  }
}

/** Whether this page was opened as a renderer (`?render=1`). */
export function isRenderMode(search: string = typeof window === 'undefined' ? '' : window.location.search): boolean {
  const raw = new URLSearchParams(search).get('render')
  return raw !== null && raw !== '0' && raw.toLowerCase() !== 'false'
}
